import { faFilterList } from "@fortawesome/pro-regular-svg-icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { IconButton } from "@aviary";
import { BrandsBox } from "@shared/components/CatalogSearch/BrandsBox";
import type {
  Brand,
  CatalogSearchType,
  IngredientFilter,
} from "@shared/components/CatalogSearch/CatalogSearch";
import { SearchFilterButton } from "@shared/components/CatalogSearch/SearchFilterButton";
import { SearchFilterTags } from "@shared/components/CatalogSearch/SearchFilterTags";
import { SearchInput } from "@shared/components/CatalogSearch/SearchInput";
import type { SearchApplyEntryPoint } from "@shared/hooks";
import {
  useAnalyticsLogger,
  useBreakpoints,
  useCatalogSearch,
  useHandleIngredientSelect,
  useHasCatalogSearchShared,
  useSharedGlobalConfig,
  AnalyticsEvent,
} from "@shared/hooks";
import { SearchType } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { l } from "@shared/locales/i18n";
import { FontAwesomeIcon } from "@shared/react-fontawesome";
import type { Typeahead } from "@shared/types/graphqlGenerated";

import * as styles from "./CatalogSearchBarWithFilters.styles";

interface Props {
  onClearFilters: () => void;
  onClearIngredients?: () => void;
  columnCount?: number;
  onClearQuery?: () => void;
  showFiltersBeforeSearch?: boolean;
  onFilterAdded?: () => void;
  placeholderOverride?: string;
  entryPointForLogging?: SearchApplyEntryPoint;
  eventNameForSearchLogging?: AnalyticsEvent;
  isEnlargeDrawerEnabled?: boolean;
  isDrawer?: boolean;
  goToProductDetails: (productId: string, productSlug?: string) => void;
  hideFilterTags?: boolean;
}

const CatalogSearchBarWithFilters = ({
  onClearFilters,
  onClearIngredients,
  columnCount,
  onClearQuery,
  showFiltersBeforeSearch,
  onFilterAdded,
  placeholderOverride,
  entryPointForLogging,
  eventNameForSearchLogging,
  isEnlargeDrawerEnabled,
  goToProductDetails,
  isDrawer,
  hideFilterTags,
  ...rest
}: Props) => {
  const {
    activeIngredients,
    setActiveIngredients,
    excludedIngredients,
    activeBrands,
    setActiveBrands,
    activeConditions,
    activeSupplementTypes,
    activeFilters,
    searchQuery,
    setSearchQuery,
    activeSearchType,
    setActiveSearchType,
    setOnlyIngredient,
    isOnlyShowFavorites,
    isOnlyShowInStock,
  } = useCatalogSearch();

  const hasSearch = useHasCatalogSearchShared();
  const { t } = useTranslation();
  const { tablet, widescreen } = useBreakpoints();
  const [searchText, setSearchText] = useState<string>(searchQuery);
  const [showSearchContent, setShowSearchContent] = useState(false);

  const { handleIngredientSelection } = useHandleIngredientSelect();
  const logAnalyticsEvent = useAnalyticsLogger();
  const { isUnauthenticated } = useSharedGlobalConfig();

  const onClearQuerySearch = () => {
    setSearchText("");
    onClearQuery?.();
  };

  const logSearchApplyEvent = () => {
    if (!entryPointForLogging || !eventNameForSearchLogging) return;
    if (isUnauthenticated) return;

    logAnalyticsEvent({
      eventName: eventNameForSearchLogging,
      payload: {
        entry_point: entryPointForLogging,
        content: searchText,
        type: SearchType.QUERY,
        search_version: 2,
      },
    });
  };

  const logTypeaheadSearchApplyEvent = (entityName, entityType) => {
    if (!entryPointForLogging || !eventNameForSearchLogging) return;
    if (isUnauthenticated) return;

    logAnalyticsEvent({
      eventName: eventNameForSearchLogging,
      payload: {
        entry_point: entryPointForLogging,
        content: entityName,
        type: entityType,
        search_version: 2,
      },
    });
  };

  const isMobile = tablet.lessThan;

  const brandsActive: boolean = activeSearchType === "brands";
  const filtersActive: boolean = activeSearchType === "filters";
  const shouldLog = entryPointForLogging && !isUnauthenticated;
  const logFilterClosedInDrawer = () => {
    if (shouldLog) {
      logAnalyticsEvent({
        eventName: AnalyticsEvent.STORE_SEARCH_CLOSE_FILTER,
        payload: {
          entry_point: `${entryPointForLogging}_Catalog_HeaderFilterButton`,
        },
      });
    }
  };

  const toggleOpenSearchFilter = (filter: CatalogSearchType) => {
    if (filter === activeSearchType) {
      setActiveSearchType("");
      logFilterClosedInDrawer();
    } else {
      setActiveSearchType(filter);
    }
    setShowSearchContent(false);
  };

  const handleOnBrandClick = (newBrand: Brand) => {
    handleBrandSelect(newBrand);
  };

  const handleSearchContentOpen = () => {
    setShowSearchContent(true);
  };

  const renderOpenSearchFilter = () => {
    if (brandsActive) {
      return (
        <BrandsBox
          columnCount={columnCount}
          onBrandClick={handleOnBrandClick}
          activeBrands={activeBrands}
        />
      );
    }
  };

  const hasMoreThanOneActiveFilter = (): boolean => {
    let totalActiveFilters = 0;

    if (isOnlyShowFavorites) {
      totalActiveFilters += 1;
    }

    if (isOnlyShowInStock) {
      totalActiveFilters += 1;
    }

    totalActiveFilters +=
      (activeBrands.length ?? 0) +
      (activeFilters.length ?? 0) +
      (activeIngredients.length ?? 0) +
      (excludedIngredients.length ?? 0) +
      (activeConditions.length ?? 0) +
      (activeSupplementTypes.length ?? 0);

    return totalActiveFilters > 1;
  };

  const onIngredientsTagClose = () => {
    setActiveIngredients([]);
    setOnlyIngredient(false);

    if (onClearIngredients) {
      onClearIngredients();
    }
  };

  const handleBrandSelect = (brandSelected: Brand) => {
    const newArrayWithoutFilter = activeBrands.filter(brand => brand.name !== brandSelected.name);

    if (newArrayWithoutFilter.length < activeBrands.length) {
      setActiveBrands(newArrayWithoutFilter);
    } else {
      setActiveBrands([...newArrayWithoutFilter, brandSelected]);
    }

    onFilterAdded?.();
  };

  const onTypeaheadSelection = (searchFilter: Pick<Typeahead, "id" | "name">) => {
    setSearchText(searchFilter?.name);
    setSearchQuery(searchFilter?.name, searchFilter?.id);
    setShowSearchContent(false);
  };

  const onQuerySearch = () => {
    logSearchApplyEvent();
    setShowSearchContent(false);
    setSearchQuery(searchText);
    onFilterAdded?.();
  };

  const clearFilters = () => {
    onClearFilters();
  };

  const onSearchValueChange = (value: string) => {
    setSearchText(value);
    setShowSearchContent(true);
  };

  const isSearchExpanded = showSearchContent && searchText;
  const inputSize = isEnlargeDrawerEnabled ? "normal" : "large";

  const renderFiltersButton = () => {
    if (hideFilterTags) {
      return null;
    }
    const filterButton =
      widescreen.lessThan && isEnlargeDrawerEnabled ? (
        <IconButton
          css={styles.filterIconButton}
          isIcon
          aria-label={t(l.sharedCommon.Filters)}
          isColor="system"
          isOutlined
          onClick={() => toggleOpenSearchFilter("filters")}
          data-e2e="filters-button"
        >
          <FontAwesomeIcon icon={faFilterList} css={styles.filterIcon} />
        </IconButton>
      ) : (
        <SearchFilterButton
          css={[styles.filtersButton, isSearchExpanded && isMobile && styles.hidden]}
          isActive={filtersActive}
          isSmall={inputSize === "normal"}
          toggleOpenSearchFilter={() => toggleOpenSearchFilter("filters")}
          data-e2e="filters-button"
        >
          {t(l.sharedCatalog.Filters)}
        </SearchFilterButton>
      );

    if (showFiltersBeforeSearch) {
      return filterButton;
    }

    if (hasSearch) {
      return filterButton;
    }

    return null;
  };

  const renderSearchFilterTags = () => {
    if (hideFilterTags) {
      return null;
    }

    return (
      <SearchFilterTags
        onIngredientsTagClose={onIngredientsTagClose}
        searchedIngredients={activeIngredients as IngredientFilter[]}
        onIngredientFilter={handleIngredientSelection}
        onClearFilters={hasMoreThanOneActiveFilter() && clearFilters}
      />
    );
  };

  return (
    <>
      <div
        css={[
          styles.searchWrapper,
          isEnlargeDrawerEnabled && isDrawer && styles.searchWrapperSticky,
        ]}
        {...rest}
      >
        <div css={styles.container.base}>
          <div css={styles.searchBarButtonContainer}>
            <SearchInput
              onSearchContentOpen={handleSearchContentOpen}
              onSearchContentClose={() => setShowSearchContent(false)}
              onQuerySearch={onQuerySearch}
              onClearQuerySearch={onClearQuerySearch}
              onTypeaheadSelection={onTypeaheadSelection}
              onSearchValueChange={onSearchValueChange}
              searchValue={searchText}
              showSearchContent={showSearchContent}
              placeholderOverride={placeholderOverride}
              goToProductDetails={goToProductDetails}
              logTypeaheadAnalytics={logTypeaheadSearchApplyEvent}
              inputSize={inputSize}
            />
          </div>
        </div>
        {renderFiltersButton()}
      </div>
      {renderOpenSearchFilter()}
      {renderSearchFilterTags()}
    </>
  );
};

export { CatalogSearchBarWithFilters };
